<script setup lang="ts">
import { X as XIcon } from '@lucide/vue'
import { computed } from 'vue'

/**
 * Removable membership tag — the emerald "collection name ×" pill shown on activity/route rows for
 * each collection a tour belongs to. The label is a button (emits `select`, e.g. open that
 * collection) and the trailing × emits `remove`. Single-root <span> so data-* / extra classes fall
 * through (the library row measures pills via `data-pill-item`).
 *
 * `size`: sm (text-[11px], the desktop/library rows) · md (text-[12px], the mobile toolbar).
 * `removable=false` drops the × for read-only contexts.
 */
interface Props {
  label: string
  size?: 'sm' | 'md'
  removable?: boolean
  /** Make the label a clickable button (default) vs plain text. */
  clickable?: boolean
  /** aria-label for the × (e.g. "Remove Montenegro 2025 from Stage 11"). */
  removeLabel?: string
}
const props = withDefaults(defineProps<Props>(), {
  size: 'sm',
  removable: true,
  clickable: true,
})
const emit = defineEmits<{ select: []; remove: [] }>()

const textClass = computed(() => (props.size === 'md' ? 'text-[12px]' : 'text-[11px]'))
</script>

<template>
  <span
    class="inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-700"
    :class="textClass"
  >
    <button
      v-if="props.clickable"
      type="button"
      class="whitespace-nowrap hover:cursor-pointer hover:underline"
      :title="props.label"
      @click.stop="emit('select')"
    >
      {{ props.label }}
    </button>
    <span v-else class="whitespace-nowrap">{{ props.label }}</span>
    <button
      v-if="props.removable"
      type="button"
      class="ml-0.5 shrink-0 rounded text-emerald-400 transition-colors hover:cursor-pointer hover:text-red-500"
      :aria-label="props.removeLabel"
      @click.stop="emit('remove')"
    >
      <XIcon class="h-2.5 w-2.5" />
    </button>
  </span>
</template>
